import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Tooltip,
  TextField,
  Autocomplete,
  Divider,
} from "@mui/material";
import {
  Build as BuildIcon,
  PlayArrow as PlayArrowIcon,
  Undo as UndoIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";
import { collection, getDocs, query, where } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import FUNCTIONS_URLS from '../../../../Config/functions';
import { adminAuthHeaders } from '../../../../Config/adminAuthHeaders';


const FIX_USER_LEDGERS_URL = FUNCTIONS_URLS.FixUserLedgersToolHttp;

const FixUserLedgersTool = () => {
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [lastBackupId, setLastBackupId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const q = query(collection(DATABASE, "users"), where("role", "==", 1));
      const snapshot = await getDocs(q);
      const users = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          email: doc.data().email || "",
          name: (doc.data().full_name || doc.data().name || "").trim() || doc.data().email || "",
        }))
        .filter((u) => u.email)
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
      setUserOptions(users);
    } catch (err) {
      console.error("Error loading users:", err);
      setError("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const analyze = async () => {
    if (!selectedUser?.email) {
      Swal.fire({ icon: "warning", title: "Select a client", text: "Pick a client from the list first." });
      return;
    }
    setError(null);
    setAnalysisResult(null);
    setLastBackupId(null);
    setAnalyzing(true);
    setProgress(20);
    try {
      const response = await axios.post(
        FIX_USER_LEDGERS_URL,
        { action: "analyze", userEmail: selectedUser.email },
        { headers: await adminAuthHeaders(), timeout: 120000 }
      );
      setProgress(100);
      const responseData = response.data;
      setAnalysisResult(responseData);
      if (responseData.totalCorrections === 0) {
        Swal.fire({
          icon: "success",
          title: "No corrections needed",
          text: "This user's ledgers are already correctly categorised.",
        });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Analysis failed";
      setError(msg);
      Swal.fire({
        icon: "error",
        title: "Analysis failed",
        text: msg,
      });
    } finally {
      setAnalyzing(false);
      setProgress(0);
    }
  };

  const execute = async () => {
    if (!selectedUser?.email || !analysisResult || analysisResult.totalCorrections === 0) {
      Swal.fire({ icon: "warning", title: "Nothing to apply", text: "Run Analyse first and ensure there are corrections." });
      return;
    }
    const confirmResult = await Swal.fire({
      title: "Apply corrections?",
      html: `
        <p>You are about to apply <strong>${analysisResult.totalCorrections}</strong> correction(s) to ledgers for <strong>${selectedUser.email}</strong>.</p>
        <p>A backup will be created before any change. You can use <strong>Undo</strong> to restore from this backup.</p>
        <p style="color:#ff9800;">This modifies ledger types and fields (Dec 2024 backwards categorisation fix).</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2af598",
      cancelButtonColor: "#666",
      confirmButtonText: "Apply",
    });
    if (!confirmResult.isConfirmed) return;

    setExecuting(true);
    setProgress(20);
    try {
      const response = await axios.post(
        FIX_USER_LEDGERS_URL,
        { action: "execute", userEmail: selectedUser.email },
        { headers: await adminAuthHeaders(), timeout: 120000 }
      );
      setProgress(100);
      const data = response.data;
      setLastBackupId(data.backupId || null);
      setAnalysisResult(null);
      Swal.fire({
        icon: "success",
        title: "Applied",
        text: `Success: ${data.successCount}, Errors: ${data.errorCount}. Backup: ${data.backupId || "—"}`,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Apply failed",
        text: err.response?.data?.error || err.message || String(err),
      });
    } finally {
      setExecuting(false);
      setProgress(0);
    }
  };

  const rollback = async () => {
    if (!selectedUser?.email || !lastBackupId) {
      Swal.fire({ icon: "warning", title: "No backup", text: "Undo is only available after a successful Apply (using the backup from that run)." });
      return;
    }
    const confirmResult = await Swal.fire({
      title: "Undo (restore from backup)?",
      html: `<p>This will restore all ledgers for <strong>${selectedUser.email}</strong> from the backup created at last Apply.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f52a2d",
      cancelButtonColor: "#666",
      confirmButtonText: "Undo",
    });
    if (!confirmResult.isConfirmed) return;

    setRollingBack(true);
    try {
      const response = await axios.post(
        FIX_USER_LEDGERS_URL,
        { action: "rollback", userEmail: selectedUser.email, backupId: lastBackupId },
        { headers: await adminAuthHeaders(), timeout: 120000 }
      );
      const data = response.data;
      setLastBackupId(null);
      Swal.fire({
        icon: "success",
        title: "Restored",
        text: `Restored: ${data.restoredCount}, Errors: ${data.errorCount}`,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Undo failed",
        text: err.response?.data?.error || err.message || String(err),
      });
    } finally {
      setRollingBack(false);
    }
  };

  const totalCorrections = analysisResult
    ? (analysisResult.typeCorrections?.length || 0) +
      (analysisResult.fieldCorrections?.length || 0) +
      (analysisResult.statusCorrections?.length || 0)
    : 0;

  return (
    <Box>
      <Typography variant="h6" sx={{ color: "#2af598", mb: 2 }}>
        Fix User Ledgers (old categorisation, Dec 2024 backwards)
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Corrects ledger <code>transection_type</code> and related fields for old data (type 1 → 2, undefined → 3, misclassified Performance, missing fields/status). Same logic as the script <code>fix-user-ledgers-complete.js</code>. Apply creates a backup; Undo restores from the last backup.
      </Alert>

      <Card sx={{ mb: 2, bgcolor: "rgba(42, 245, 152, 0.05)", border: "1px solid rgba(42, 245, 152, 0.2)" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={userOptions}
                getOptionLabel={(opt) => opt.name ? `${opt.name} (${opt.email})` : opt.email || ""}
                value={selectedUser}
                onChange={(_, v) => { setSelectedUser(v); setAnalysisResult(null); setLastBackupId(null); }}
                loading={loadingUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client (name A–Z)"
                    variant="outlined"
                    size="small"
                    placeholder="Select client by name"
                    sx={{ "& .MuiOutlinedInput-root": { color: "#eee" }, "& label": { color: "#aaa" } }}
                  />
                )}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.id}>
                    <Box>
                      <Typography variant="body1" sx={{ color: "#eee" }}>{opt.name || opt.email}</Typography>
                      <Typography variant="caption" sx={{ color: "#888" }}>{opt.email}</Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  onClick={analyze}
                  disabled={!selectedUser || analyzing}
                  sx={{ bgcolor: "#2af598", color: "#111", "&:hover": { bgcolor: "#25d885" } }}
                >
                  Analyse
                </Button>
                <Button
                  variant="outlined"
                  startIcon={executing ? <CircularProgress size={20} color="inherit" /> : <BuildIcon />}
                  onClick={execute}
                  disabled={!analysisResult || totalCorrections === 0 || executing}
                  sx={{ borderColor: "#2af598", color: "#2af598", "&:hover": { borderColor: "#25d885", bgcolor: "rgba(42, 245, 152, 0.1)" } }}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  startIcon={rollingBack ? <CircularProgress size={20} color="inherit" /> : <UndoIcon />}
                  onClick={rollback}
                  disabled={!lastBackupId || rollingBack}
                  sx={{ borderColor: "#f52a2d", color: "#f52a2d", "&:hover": { borderColor: "#e01f22", bgcolor: "rgba(245, 42, 45, 0.1)" } }}
                >
                  Undo
                </Button>
              </Box>
            </Grid>
          </Grid>
          {progress > 0 && progress < 100 && (
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {analysisResult && totalCorrections > 0 && (
        <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
              Corrections to be applied ({totalCorrections} total)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              User: {analysisResult.userEmail} · Ledgers: {analysisResult.ledgerCount}
            </Typography>

            {analysisResult.typeCorrections?.length > 0 && (
              <>
                <Typography variant="overline" sx={{ color: "#aaa" }}>Type corrections</Typography>
                <TableContainer component={Paper} sx={{ mb: 2, bgcolor: "transparent" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#aaa" }}>Date</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Description</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Old → New</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisResult.typeCorrections.map((c, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ color: "#ccc" }}>{c.date}</TableCell>
                          <TableCell sx={{ color: "#ccc" }}>{c.description}</TableCell>
                          <TableCell><Chip size="small" label={`${c.old_type} → ${c.new_type}`} sx={{ bgcolor: "rgba(42,245,152,0.2)", color: "#2af598" }} /></TableCell>
                          <TableCell sx={{ color: "#ccc" }}>${Number(c.amount || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {analysisResult.fieldCorrections?.length > 0 && (
              <>
                <Typography variant="overline" sx={{ color: "#aaa" }}>Field corrections</Typography>
                <TableContainer component={Paper} sx={{ mb: 2, bgcolor: "transparent" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#aaa" }}>Date</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Ledger id</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Updates</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisResult.fieldCorrections.map((c, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ color: "#ccc" }}>{c.date}</TableCell>
                          <TableCell sx={{ color: "#ccc", fontFamily: "monospace", fontSize: "0.75rem" }}>{c.id}</TableCell>
                          <TableCell sx={{ color: "#ccc", fontSize: "0.75rem" }}>{JSON.stringify(c.updateData)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {analysisResult.statusCorrections?.length > 0 && (
              <>
                <Typography variant="overline" sx={{ color: "#aaa" }}>Status corrections</Typography>
                <TableContainer component={Paper} sx={{ bgcolor: "transparent" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#aaa" }}>Date</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Description</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Update</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisResult.statusCorrections.map((c, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ color: "#ccc" }}>{c.date}</TableCell>
                          <TableCell sx={{ color: "#ccc" }}>{c.description}</TableCell>
                          <TableCell sx={{ color: "#ccc", fontSize: "0.75rem" }}>{JSON.stringify(c.updateData)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {lastBackupId && (
        <Alert severity="success" sx={{ mt: 1 }}>
          Last backup ID (for Undo): <code>{lastBackupId}</code>
        </Alert>
      )}
    </Box>
  );
};

export default FixUserLedgersTool;
